import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { BucketCard } from "@/components/BucketCard";
import { BucketDetails } from "@/components/BucketDetails";
import { BucketCoverField } from "@/components/BucketCover";
import { toSentenceCase } from "@/lib/utils";
import { Plus, Sparkles, Search, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

const empty = { title: "", notes: "", status: "dreaming", cover_id: null, cover_crop: null };
const itemCover = (item) => item.cover || (item.image_path ? { source: "upload", file_ref: item.image_path } : null);

export const BucketList = ({ refreshKey }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [statusSavingId, setStatusSavingId] = useState(null);
  const [movePrompt, setMovePrompt] = useState(null);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState("");
  const moveFocus = useRef(null);
  const doneAfterEdit = useRef(null);
  const [draftCover, setDraftCover] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const resumeFocus = useRef(null);
  const [detailId, setDetailId] = useState(null);
  const detailTrigger = useRef(null);
  const detailItem = items.find((item) => item.id === detailId);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const shown = !q ? items : items.filter((item) => [item.title, item.location, item.notes].filter(Boolean).join(" ").toLowerCase().includes(q));
  const originalCover = editingItem ? itemCover(editingItem) : null;
  const coverChanged = (draftCover?.id || draftCover?.file_ref || null) !== (originalCover?.id || originalCover?.file_ref || null);
  const cropChanged = JSON.stringify(form.cover_crop) !== JSON.stringify(editingItem?.cover_crop || null);
  const hasUnsavedChanges = form.title !== (editingItem?.title ?? empty.title)
    || form.notes !== (editingItem?.notes || empty.notes)
    || form.status !== (editingItem?.status ?? empty.status)
    || coverChanged || cropChanged;

  const load = async () => {
    const res = await api.get("/bucket");
    setItems(res.data);
  };
  useEffect(() => { load(); }, [refreshKey]);

  const updateItem = (updated) => setItems((current) => current.map((item) => item.id === updated.id ? updated : item));
  const changeStatus = async (item, status) => {
    setStatusSavingId(item.id);
    try {
      const { data } = await api.put(`/bucket/${item.id}`, { status });
      updateItem(data);
      return data;
    } finally { setStatusSavingId(null); }
  };
  const offerMove = (item, trigger) => {
    moveFocus.current = trigger || document.querySelector('[data-testid="bucket-detail-status-icon"]');
    setMoveError(""); setMovePrompt(item);
  };
  const moveToBottom = async () => {
    if (moving) return;
    setMoving(true); setMoveError("");
    try {
      const { data } = await api.post(`/bucket/${movePrompt.id}/move-bottom`);
      setItems((current) => [...current.filter((item) => item.id !== data.id), data]);
      setMovePrompt(null);
    } catch { setMoveError("Could not confirm the move. Your item is still marked Done. Please try again."); }
    finally { setMoving(false); }
  };
  const closeDetails = () => {
    setDetailId(null);
    requestAnimationFrame(() => (detailTrigger.current?.isConnected ? detailTrigger.current : document.querySelector('[data-testid="add-bucket-btn"]'))?.focus());
  };
  const editItem = (item) => {
    setEditingItem(item);
    setForm({ title: item.title, notes: item.notes || "", status: item.status, cover_id: item.cover?.id || null, cover_crop: item.cover_crop || null });
    setDraftCover(itemCover(item));
    setFormError(""); setDetailId(null); setOpen(true);
  };
  const closeForm = () => {
    setConfirmDiscard(false); setOpen(false);
    if (editingItem) setDetailId(editingItem.id);
    setEditingItem(null); setForm(empty); setDraftCover(null); setFormError("");
  };
  const onFormOpenChange = (next) => {
    if (next) { setOpen(true); return; }
    if (saving || coverBusy || confirmDiscard) return;
    if (hasUnsavedChanges) {
      resumeFocus.current = document.activeElement;
      setConfirmDiscard(true);
    } else closeForm();
  };
  const saveItem = async () => {
    if (!form.title.trim() || saving || coverBusy) return;
    setSaving(true); setFormError("");
    try {
      if (editingItem) {
        const changes = {};
        if (form.title !== editingItem.title) changes.title = toSentenceCase(form.title);
        if (form.notes !== (editingItem.notes || "")) changes.notes = toSentenceCase(form.notes);
        if (form.status !== editingItem.status) changes.status = form.status;
        if (coverChanged) changes.cover_id = draftCover?.id || null;
        if (coverChanged || cropChanged) changes.cover_crop = form.cover_crop;
        if (Object.keys(changes).length) {
          const { data } = await api.put(`/bucket/${editingItem.id}`, changes);
          updateItem(data);
          if (changes.status === "done" && editingItem.status !== "done") doneAfterEdit.current = data;
        }
        setDetailId(null);
      } else {
        await api.post("/bucket", { ...form, title: form.title, notes: toSentenceCase(form.notes) });
        load();
      }
      setForm(empty); setDraftCover(null); setEditingItem(null); setOpen(false);
    } catch (error) {
      setFormError(typeof error.response?.data?.detail === "string" ? error.response.data.detail : "Could not save this idea. Please try again.");
    } finally { setSaving(false); }
  };

  const chooseCover = (cover, crop = null) => {
    setDraftCover(cover);
    setForm((current) => ({ ...current, cover_id: cover?.id || null, cover_crop: crop }));
  };

  return (
    <div data-testid="bucket-list-panel" className="space-y-4">
      <div data-testid="bucket-header" className="mobile-section-header flex items-center justify-between gap-3 w-full px-5 py-4 rounded-3xl bg-white/95 backdrop-blur-md border border-white/60 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
        <div className="min-w-0">
          <h2 data-testid="bucket-heading" className="text-2xl sm:text-3xl font-bold font-['Nunito'] text-stone-900 tracking-tight">Bucket List</h2>
          <p data-testid="bucket-tagline" className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400 mt-1">Dream destinations &amp; Experiences</p>
        </div>
        <Dialog open={open} onOpenChange={onFormOpenChange}>
          <DialogTrigger asChild>
            <Button data-testid="add-bucket-btn" size="icon" aria-label="Add dream" title="Add dream" className="rounded-full bg-ac hover:bg-ac-hover text-white shrink-0 hover:-translate-y-0.5 transition-[transform,background-color]">
              <Plus className="w-5 h-5" strokeWidth={3} aria-hidden="true" />
            </Button>
          </DialogTrigger>
          <DialogContent data-testid={editingItem ? "edit-bucket-dialog" : "add-bucket-dialog"} aria-describedby={undefined} onCloseAutoFocus={(event) => { if (doneAfterEdit.current) { event.preventDefault(); offerMove(doneAfterEdit.current); doneAfterEdit.current = null; } }} className="rounded-3xl font-['Figtree'] max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle data-testid="bucket-form-heading" className="font-['Nunito'] text-2xl">{editingItem ? "Edit bucket item" : "Add to bucket list"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <BucketCoverField title={form.title} current={draftCover} currentCrop={form.cover_crop} onChoose={chooseCover} onBusyChange={setCoverBusy} disabled={saving} editingWithCover={Boolean(editingItem && itemCover(editingItem))} />
              <div data-testid="bucket-idea-section" className="space-y-2">
                <Label htmlFor="bucket-title-input" data-testid="bucket-title-label" className="font-bold text-stone-700">Idea / Destination <span aria-hidden="true" className="text-rose-500">*</span></Label>
                <Input id="bucket-title-input" data-testid="bucket-title-input" required aria-required="true" placeholder="e.g. See the Northern Lights" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl" />
              </div>
              <div data-testid="bucket-notes-section" className="space-y-2">
                <Label htmlFor="bucket-notes-input" data-testid="bucket-notes-label" className="font-bold text-stone-700">Notes <span className="font-normal text-stone-400">(optional)</span></Label>
                <Textarea id="bucket-notes-input" data-testid="bucket-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl" />
              </div>
              <div data-testid="bucket-status-section" className="space-y-2">
                <Label htmlFor="bucket-status-input" data-testid="bucket-status-label" className="font-bold text-stone-700">Status</Label>
                <Select value={form.status} onValueChange={(status) => setForm((current) => ({ ...current, status }))}>
                  <SelectTrigger id="bucket-status-input" data-testid="bucket-status-input" className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent data-testid="bucket-status-options" className="rounded-xl bg-white">
                    <SelectItem data-testid="bucket-status-dreaming" value="dreaming">Dreaming</SelectItem>
                    <SelectItem data-testid="bucket-status-planned" value="planned">Planned</SelectItem>
                    <SelectItem data-testid="bucket-status-done" value="done">Done</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {formError && <p data-testid="bucket-form-error" role="alert" className="text-sm text-rose-600">{formError}</p>}
            <DialogFooter>
              <Button data-testid="save-bucket-btn" onClick={saveItem} disabled={saving || coverBusy || !form.title.trim()} className="rounded-full bg-ac hover:bg-ac-hover font-bold w-full">{saving ? "Saving…" : editingItem ? "Save changes" : "Save dream"}</Button>
            </DialogFooter>
            <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
              <AlertDialogContent data-testid="bucket-unsaved-dialog" className="w-[calc(100%-2rem)] max-w-sm rounded-3xl font-['Figtree']" onEscapeKeyDown={(event) => event.stopPropagation()} onCloseAutoFocus={(event) => {
                event.preventDefault();
                if (open) (resumeFocus.current?.isConnected && resumeFocus.current.closest('[role="dialog"]') ? resumeFocus.current : document.getElementById("bucket-title-input"))?.focus();
              }}>
                <AlertDialogHeader className="text-left">
                  <AlertDialogTitle data-testid="bucket-unsaved-title" className="font-['Nunito']">Discard unsaved changes?</AlertDialogTitle>
                  <AlertDialogDescription data-testid="bucket-unsaved-description">Your changes haven’t been saved. Keep editing, or discard them and close this form.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex-row gap-2 sm:space-x-0">
                  <AlertDialogCancel data-testid="bucket-unsaved-keep-editing" className="mt-0 flex-1 rounded-full font-bold">Keep editing</AlertDialogCancel>
                  <AlertDialogAction data-testid="bucket-unsaved-discard" onClick={closeForm} className="flex-1 rounded-full bg-rose-600 hover:bg-rose-700 text-white font-bold">Discard changes</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DialogContent>
        </Dialog>
      </div>

      <div data-testid="bucket-search-panel" className="relative w-full rounded-3xl bg-white/95 backdrop-blur-md border border-white/60 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
        <Search aria-hidden="true" className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
        <Input data-testid="bucket-search-input" aria-label="Search bucket list items by name, location, or notes" value={query} onChange={(event) => setQuery(event.target.value)} className="rounded-3xl pl-12 pr-10 h-14 bg-transparent border-0 shadow-none text-sm" />
        {query && <button type="button" data-testid="bucket-search-clear" aria-label="Clear search" title="Clear search" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors"><X aria-hidden="true" className="w-4 h-4" /></button>}
      </div>

      {shown.length === 0 ? (
        <EmptyBucket isSearch={!!q} />
      ) : (
        <div data-testid="bucket-cards" className="columns-2 md:columns-3 xl:columns-4 gap-3 sm:gap-4 w-full">
          {shown.map((item, i) => <BucketCard key={item.id} item={item} index={i} onStatusChange={changeStatus} onDone={offerMove} statusSaving={statusSavingId === item.id} onOpen={(selected, trigger) => { detailTrigger.current = trigger; setDetailId(selected.id); }} />)}
        </div>
      )}
      {detailItem && <BucketDetails key={detailItem.id} item={detailItem} onStatusChange={changeStatus} onDone={offerMove} statusSaving={statusSavingId === detailItem.id} onClose={closeDetails} onEdit={editItem} onDeleted={(id) => { setItems((current) => current.filter((item) => item.id !== id)); closeDetails(); }} />}
      {movePrompt && <DoneMovePrompt item={movePrompt} busy={moving} error={moveError} onKeep={() => setMovePrompt(null)} onMove={moveToBottom} onCloseAutoFocus={(event) => {
        event.preventDefault();
        (moveFocus.current?.isConnected ? moveFocus.current : document.querySelector('[data-testid="bucket-detail-status-icon"], [data-testid="add-bucket-btn"]'))?.focus();
      }} />}
    </div>
  );
};

const DoneMovePrompt = ({ item, busy, error, onKeep, onMove, onCloseAutoFocus }) => <AlertDialog open onOpenChange={(open) => { if (!open && !busy) onKeep(); }}>
  <AlertDialogContent data-testid="bucket-done-move-dialog" aria-labelledby="bucket-done-move-title" aria-describedby="bucket-done-move-title" className="w-[calc(100%-2rem)] max-w-sm rounded-3xl font-['Figtree']" onCloseAutoFocus={onCloseAutoFocus} onEscapeKeyDown={(event) => { event.stopPropagation(); if (busy) event.preventDefault(); }}>
    <AlertDialogHeader className="text-left">
      <AlertDialogTitle id="bucket-done-move-title" data-testid="bucket-done-move-title" className="font-['Nunito']">Move this item to the bottom of your Bucket List?</AlertDialogTitle>
    </AlertDialogHeader>
    {error && <p data-testid="bucket-done-move-error" role="alert" className="text-sm text-rose-600">{error}</p>}
    <AlertDialogFooter className="flex-row gap-2 sm:space-x-0">
      <AlertDialogCancel data-testid="bucket-done-keep-here" disabled={busy} className="mt-0 flex-1 rounded-full font-bold">Keep here</AlertDialogCancel>
      <AlertDialogAction data-testid="bucket-done-move-bottom" disabled={busy} onClick={(event) => { event.preventDefault(); onMove(); }} className="flex-1 rounded-full bg-ac hover:bg-ac-hover font-bold">{busy ? "Moving…" : "Move to bottom"}</AlertDialogAction>
    </AlertDialogFooter>
  </AlertDialogContent>
</AlertDialog>;

const EmptyBucket = ({ isSearch = false }) => (
  <div data-testid="bucket-empty-state" role="status" className="w-full rounded-3xl border border-white/60 bg-white/95 backdrop-blur-md shadow-[0_8px_24px_rgba(0,0,0,0.14)] py-16 flex flex-col items-center text-center px-6">
    <span className="w-14 h-14 rounded-full bg-rose-100 text-rose-500 grid place-items-center mb-4">
      <Sparkles className="w-7 h-7" strokeWidth={2.5} />
    </span>
    <h3 data-testid="bucket-empty-title" className="text-xl font-bold font-['Nunito'] text-stone-900">{isSearch ? "No matches" : "No dreams yet"}</h3>
    <p data-testid="bucket-empty-message" className="text-stone-500 mt-1 max-w-xs">{isSearch ? "Try searching for a different destination or note." : "Start collecting the places and experiences you can't wait to tick off."}</p>
  </div>
);
